import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const authHeaders = (token, tenantId) => ({
  'Authorization': `Bearer ${token}`,
  'X-Tenant-ID': tenantId,
  'Content-Type': 'application/json',
});

const fetchPrescriptions = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/pharmacy/prescriptions`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const STATUS_META = {
  PENDING:  { label: 'Pending Review',  color: '#854f0b', bg: '#faeeda', border: '#fac775' },
  APPROVED: { label: 'Approved', color: '#3b6d11', bg: '#eaf3de', border: '#97c459' },
  REJECTED: { label: 'Rejected', color: '#a32d2d', bg: '#fcebeb', border: '#f09595' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.round((Date.now() - new Date(iso)) / 60000);
  if (diff < 1)    return 'just now';
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 1440)}d ago`;
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META['PENDING'];
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
      background: m.bg, color: m.color, border: `0.5px solid ${m.border}`,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
};

const SkeletonCard = () => (
  <div style={{ background: '#fff', border: '0.5px solid #e4e2db', borderRadius: 14, padding: '1rem 1.25rem' }}>
    {[['60%', 14], ['40%', 12], ['100%', 32]].map(([w, h], i) => (
      <div key={i} style={{ width: w, height: h, borderRadius: 6, marginBottom: 10, background: 'linear-gradient(90deg,#f0eeea 25%,#e8e6e1 50%,#f0eeea 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
    ))}
  </div>
);

const PrescriptionCard = ({ rx, idx }) => (
  <div style={{
    background: '#fff',
    border: `0.5px solid ${rx.status === 'APPROVED' ? '#97c459' : rx.status === 'REJECTED' ? '#f09595' : '#e4e2db'}`,
    borderRadius: 14, padding: '1rem 1.25rem',
    animation: `fadeUp .3s ${idx * 40}ms ease both`,
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
      <div>
        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 14, color: '#1e1b4b', marginBottom: 3 }}>
          {rx.patientName || 'Patient'}
        </div>
        <div className="t-meta">{rx.patientPhone}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <StatusBadge status={rx.status} />
        <span className="t-meta" style={{ fontSize: 11 }}>{timeAgo(rx.uploadedAt)}</span>
      </div>
    </div>

    {/* File */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#f5f4f0', borderRadius: 8, border: '0.5px solid #e4e2db' }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: '#e6f1fb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {rx.fileType === 'application/pdf' ? '📄' : '🖼️'}
      </div>
      <span style={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1a1917' }}>
        {rx.fileName || 'Prescription file'}
      </span>
    </div>

    {/* Doctor assignment */}
    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {rx.assignedDoctorName ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#f0f9ff', border: '0.5px solid #bae6fd',
          borderRadius: 8, padding: '5px 10px', fontSize: 12,
          color: '#0369a1', fontFamily: 'DM Sans, sans-serif',
        }}>
          <span>👨‍⚕️</span>
          <span>Assigned to <strong>{rx.assignedDoctorName}</strong></span>
        </div>
      ) : rx.status === 'PENDING' ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#fafaf9', border: '0.5px solid #e4e2db',
          borderRadius: 8, padding: '5px 10px', fontSize: 12,
          color: '#78716c', fontFamily: 'DM Sans, sans-serif',
        }}>
          <span>⏳</span>
          <span>Awaiting doctor assignment</span>
        </div>
      ) : null}
    </div>

    {rx.notes && (
      <div style={{ marginTop: 10, fontSize: 12, fontStyle: 'italic', color: '#6b6860', borderLeft: '2px solid #e4e2db', paddingLeft: 10 }}>
        "{rx.notes}"
      </div>
    )}
  </div>
);

const TABS = ['PENDING', 'APPROVED', 'REJECTED'];

const Prescriptions = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { tenant } = useTenant();

  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('PENDING');

  const load = useCallback(async () => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPrescriptions(token, tenant.id);
      setPrescriptions(data);
    } catch (e) {
      setError(`Could not load prescriptions (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  const counts = {
    PENDING:  prescriptions.filter(p => p.status === 'PENDING').length,
    APPROVED: prescriptions.filter(p => p.status === 'APPROVED').length,
    REJECTED: prescriptions.filter(p => p.status === 'REJECTED').length,
  };
  const filtered = prescriptions.filter(p => p.status === tab);

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Prescriptions</span>
        </div>

        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <h1 className="t-h1" style={{ marginBottom: 4 }}>℞ Prescriptions</h1>
          <p className="t-body-sm">Patient prescription submissions</p>
        </div>

        <div className="stat-grid fade-up-1" style={{ marginBottom: '1.5rem' }}>
          {TABS.map(t => {
            const m = STATUS_META[t];
            return (
              <div key={t} className="stat-card"
                style={{ borderLeft: `3px solid ${m.color}`, cursor: 'pointer' }}
                onClick={() => setTab(t)}
              >
                <div style={{ fontSize: 22, marginBottom: 8 }}>
                  {t === 'PENDING' ? '⏳' : t === 'APPROVED' ? '✅' : '❌'}
                </div>
                <div className="stat-value" style={{ color: m.color }}>{loading ? '—' : counts[t]}</div>
                <div className="stat-label">{m.label}</div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', background: '#f5f4f0', borderRadius: 10, padding: 3, width: 'fit-content' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              fontSize: 13, padding: '5px 16px', borderRadius: 8, border: 'none',
              cursor: 'pointer', transition: 'all .15s', fontFamily: 'DM Sans, sans-serif',
              background: tab === t ? '#fff' : 'transparent',
              color: tab === t ? '#1a1917' : '#6b6860',
              fontWeight: tab === t ? 500 : 400,
              boxShadow: tab === t ? '0 0 0 0.5px #e4e2db' : 'none',
            }}>
              {t.charAt(0) + t.slice(1).toLowerCase()}
              {counts[t] > 0 && (
                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, background: STATUS_META[t].bg, color: STATUS_META[t].color, padding: '1px 6px', borderRadius: 20 }}>
                  {counts[t]}
                </span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : filtered.length === 0
              ? (
                <div className="empty-state">
                  <div className="empty-icon">℞</div>
                  <p className="empty-title">No {tab.toLowerCase()} prescriptions</p>
                </div>
              )
              : filtered.map((rx, i) => <PrescriptionCard key={rx.id} rx={rx} idx={i} />)
          }
        </div>

      </main>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  );
};

export default Prescriptions;